import React, { useState, useEffect, useCallback } from 'react';
import { Bus } from 'lucide-react';
import EmptyState from '../common/EmptyState';
import HireDetail from './HireDetail';
import { AdminHireAPI } from '../../services/api';
import {
  HIRE_FILTERS, PAYMENT_STATE_LABELS, NEEDS_ACTION_LABELS, formatRand, formatRange, hireStatusLabel, apiErrorMessage,
} from './charterUtils';

/**
 * Hires (spec 6.4): every charter hire with its operator, traveller, dates,
 * status, payment and total. Filters All, Requested, Confirmed, Needs action
 * and Completed. A row opens the detail. The header shows what has been
 * taken over every hire the filter matches, from the server, since the list
 * itself stops at 200 rows (spec 6.5: "Taken", not "Held on cards").
 */
const HireList = ({ isSuperAdmin }) => {
  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ takenCents: 0, total: 0, truncated: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AdminHireAPI.list({ filter });
      const data = res.data.data;
      setItems(data.items || []);
      setTotals({ takenCents: data.takenCents || 0, total: data.total || 0, truncated: Boolean(data.truncated) });
      setError('');
    } catch (e) {
      setItems([]);
      setTotals({ takenCents: 0, total: 0, truncated: false });
      setError(apiErrorMessage(e, 'Could not load hires'));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  if (selectedId) {
    return <HireDetail hireId={selectedId} isSuperAdmin={isSuperAdmin} onBack={() => { setSelectedId(null); load(); }} />;
  }

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hires</h1>
          <p className="text-sm text-gray-500">Charter bookings. Payments run in simulation.</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-2 text-right">
          <div className="text-xs text-gray-500">Taken on {totals.total} {totals.total === 1 ? 'hire' : 'hires'}</div>
          <div className="text-lg font-semibold text-gray-900" data-testid="taken-total">{formatRand(totals.takenCents)}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {HIRE_FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${filter === f.id ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <p className="mb-4 text-sm text-red-600" role="alert">{error}</p>}
      {!loading && totals.truncated && (
        <p className="mb-4 text-sm text-yellow-700" data-testid="list-truncated">Showing the first {items.length} of {totals.total}. The total above covers all of them.</p>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading && <div className="p-6 text-sm text-gray-500">Loading...</div>}
        {!loading && items.length === 0 && <EmptyState title="No hires" message="Nothing matches this filter." icon={Bus} />}
        {!loading && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Code', 'Operator', 'Traveller', 'Dates', 'Status', 'Payment', 'Total'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((h) => (
                  <tr key={h._id} onClick={() => setSelectedId(h._id)} className="hover:bg-gray-50 cursor-pointer">
                    <td className="px-4 py-3 font-mono font-medium text-gray-900">
                      <button onClick={(e) => { e.stopPropagation(); setSelectedId(h._id); }} className="hover:underline">{h.bookingCode}</button>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{h.operator && h.operator.companyName}</td>
                    <td className="px-4 py-3 text-gray-700">{h.traveler && h.traveler.name}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatRange(h.startAt, h.endAt)}</td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900">{hireStatusLabel(h)}</div>
                      {(h.needsAction || []).map((r) => (
                        <span key={r} className="inline-block mt-1 mr-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800">{NEEDS_ACTION_LABELS[r] || r}</span>
                      ))}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{PAYMENT_STATE_LABELS[h.paymentState] || h.paymentState}</td>
                    <td className="px-4 py-3 text-gray-900 whitespace-nowrap">{formatRand(h.totalCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default HireList;
